import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DownloadIcon from '@mui/icons-material/Download'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import InfoIcon from '@mui/icons-material/Info'
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import React, { useCallback, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as XLSX from 'xlsx'
import { borrarLineasExcel, importarLineasExcel } from '../../actions/excelPedido'
import { postLineas } from '../../actions/pedidos'
import i18n from '../../lang/idiomas'
import Spinner from '../layout/Spinner'
import ExcelPedidoTabla from './ExcelPedidoTabla'

const ExcelPedido = props => {
  const dispatch = useDispatch()

  const [file, setFile] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef(null)

  const isLoading = useSelector(state => state.excelPedido.isLoading)
  const lineas = useSelector(state => state.excelPedido.lineasExcel)
  const pedido = useSelector(state => state.pedidos.pedido)
  const isLoadingCarrito = useSelector(state => state.pedidos.isLoadingLines)

  const VisuallyHiddenInput = styled('input')({
    display: 'none',
  })

  const handleFile = fileUploaded => {
    const readFile = file => {
      return new Promise(function (resolve, reject) {
        let fr = new FileReader()

        fr.onload = function () {
          const excel = XLSX.read(fr.result, { type: 'binary' })
          const sheet = excel.Sheets[excel.SheetNames[0]]

          // Convertimos el contenido en un array de arrays
          const rawData = XLSX.utils.sheet_to_json(sheet, { header: 1 })

          if (rawData.length < 2) {
            resolve([]) // Si no hay suficientes datos, devolvemos un array vacío
            return
          }

          // Mapeamos las filas sin la cabecera (primera fila) a objetos con claves fijas "articulo" y "cantidad"
          const jsonData = rawData
            .slice(1)
            .filter(row => row[0] !== undefined && row[1] !== undefined) // Filtrar filas sin datos
            .map((row, index) => ({
              articulo: row[0], // Primera columna
              cantidad: row[1], // Segunda columna
              __rowNum__: index + 2, // Sumar 2 porque la primera fila es el encabezado (fila 1 en Excel),
            }))
          resolve(jsonData)
        }
        fr.onerror = function () {
          reject(fr)
        }

        fr.readAsBinaryString(file)
      })
    }

    if (fileUploaded) {
      setFile(fileUploaded)
      let fileContent = readFile(fileUploaded)

      fileContent.then(value => {
        dispatch(importarLineasExcel(value))
        setFile(null)
      })
    }
  }

  const loadFile = event => {
    const fileUploaded = event.target.files[0]
    handleFile(fileUploaded)
  }

  const downloadTemplate = () => {
    const templateUrl = '/assets/plantilla.xlsx'
    const link = document.createElement('a')
    link.href = templateUrl
    link.download = 'plantilla.xlsx'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const onDragOver = useCallback(event => {
    event.preventDefault()
    setIsDragging(true)
  }, [])

  const onDragLeave = useCallback(event => {
    event.preventDefault()
    setIsDragging(false)
  }, [])

  const onDrop = useCallback(event => {
    event.preventDefault()
    setIsDragging(false)
    const fileUploaded = event.dataTransfer.files[0]
    handleFile(fileUploaded)
    // eslint-disable-next-line
  }, [])

  const handleBoxClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleConfirmationClick = () => {
    if (!isLoading) {
      let lineasPedido = []
      const lineasValidas = lineas.filter(
        item => item['validez'] === 'S' || item['validez'] === 'W',
      )

      for (const i in lineasValidas) {
        let linea = {
          articulo: lineasValidas[i]?.articulo,
          precio_venta: parseFloat(lineasValidas[i].precio),
          estado_linea: 'B',
          cantidad_pedida: lineasValidas[i].cantidad,
          //presentacion_pedido: presentacion.presentacion || articulo.presentacion_web,
          tipo_linea: 'P',
          sub_pres: '',
          sub_pres_cant: '',
          sub_pres_fc: '',
          presentacion_escogida: '',
          pres_fc: '',
        }
        lineasPedido.push(linea)
      }
      dispatch(postLineas(lineasPedido, '', pedido, null, 'S'))
      dispatch(borrarLineasExcel())
    }
  }

  const handleCancelClick = e => {
    e.preventDefault()
    dispatch(borrarLineasExcel())
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          marginTop: 4,
        }}>
        <Button
          variant='outlined'
          startIcon={<DownloadIcon />}
          onClick={downloadTemplate}>
          {i18n.t('excel.desplant')}
        </Button>
        {(isLoadingCarrito || isLoading) && (
          <>
            <Spinner showSpinner={isLoading || isLoadingCarrito}></Spinner>
          </>
        )}
        {lineas.length > 0 && (
          <Box>
            <div style={{ paddingBottom: '1rem' }}>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls='panel1-content'
                  id='panel1-header'
                  sx={{ backgroundColor: '#f5f5f5' }}>
                  <Typography
                    component='span'
                    sx={{ display: 'flex', gap: '5px' }}>
                    <InfoIcon /> Leyenda colores
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: '#f5f5f5' }}>
                  {/* <ul>
                    <li style={{ paddingBottom: '5px' }}>
                      <span style={{ background: '#f3edc7' }}>
                        Amarillo: Artículo correcto con indicencias
                      </span>
                    </li>
                    <li>
                      <span style={{ background: '#f3c8cec2' }}>
                        Rojo: Articulo incorrecto, la línea no será añadida al pedido
                      </span>
                    </li>
                  </ul> */}
                  <Stack
                    direction='column'
                    spacing={1}>
                    <Chip
                      label='Artículo correcto con indicencias'
                      sx={{ backgroundColor: '#f3edc7', color: '#000' }}
                    />
                    <Chip
                      label='Articulo incorrecto, la línea no será añadida al pedido'
                      sx={{ backgroundColor: '#f3c8cec2', color: '#000' }}
                    />
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </div>
            <Box sx={{ textAlign: 'center' }}>
              <TableContainer component={Paper}>
                <Table aria-label='simple table'>
                  <TableHead>
                    <TableRow>
                      <TableCell>{i18n.t('general.articulo')}</TableCell>
                      <TableCell>{i18n.t('general.descrip')}</TableCell>
                      <TableCell align='center'>{i18n.t('plantillas.pcantid')}</TableCell>
                      <TableCell align='right'>{i18n.t('general.precio') + '/u'}</TableCell>
                      <TableCell align='center' />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lineas.map(linea => (
                      <ExcelPedidoTabla
                        key={linea.articulo}
                        lin={linea}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Button
                variant='contained'
                sx={{ marginTop: 5 }}
                onClick={handleConfirmationClick}>
                {i18n.t('excel.addlinva')}
              </Button>
              <Button
                variant='outlined'
                sx={{ marginTop: 5, marginLeft: 2 }}
                onClick={handleCancelClick}>
                {i18n.t('excel.cancimp')}
              </Button>
            </Box>
          </Box>
        )}
        {!file && lineas.length <= 0 && !isLoading && !isLoadingCarrito && (
          <Box
            component='label'
            htmlFor='file-input'
            sx={{
              position: 'relative',
              width: '800px',
              height: '400px',
              border: '2px dashed',
              borderColor: isDragging ? '#4caf50' : '#68935b',
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: 'auto',
              flexDirection: 'column',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              backgroundColor: isDragging ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(104, 147, 91, 0.1)',
              },
            }}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={handleBoxClick}>
            <CloudUploadIcon style={{ fontSize: 48, marginBottom: '16px', color: '#68935b' }} />
            <span>{i18n.t('excel.dropfile')}</span>
            <VisuallyHiddenInput
              ref={fileInputRef}
              type='file'
              onChange={loadFile}
              accept='.xlsx,.xls'
            />
          </Box>
        )}
      </Box>
    </>
  )
}

export default ExcelPedido

/** */
